import { Component, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FracScheduleModel } from '../model';

export interface TimelineRow {
  label: string; // operator name or region depending on sortBy
  wells: TimelineBar[];
  rowCount: number; // how many stacked bar rows this operator needs
}

export interface TimelineBar {
  model: FracScheduleModel;
  leftPct: number;
  widthPct: number;
  color: string;
  stackRow: number; // vertical stacking index within operator row
}

export interface MonthLine {
  leftPct: number;
  label: string; // e.g. "Mar 2025"
}

type ZoomPreset = '30d' | '60d' | '90d' | 'all';

@Component({
  selector: 'frac-calendar',
  standalone: false,
  templateUrl: './frac-calendar.component.html',
  styleUrl: './frac-calendar.component.scss'
})
export class FracCalendarComponent implements OnChanges {

  @Input() data: FracScheduleModel[] = [];
  @ViewChild('calScroll') calScrollRef!: ElementRef<HTMLDivElement>;

  sortBy: 'operator' | 'region' = 'operator';
  zoomPreset: ZoomPreset = 'all';

  rows: TimelineRow[] = [];
  dateHeaders: { label: string; leftPct: number }[] = [];
  monthLines: MonthLine[] = [];
  todayPct = 0;

  selectedBar: TimelineBar | null = null;
  detailPanelStyle = { top: '0px', left: '0px' };

  private collapsed = new Set<string>();

  get groupLabel(): string {
    return this.sortBy === 'operator' ? 'Operator' : 'Region';
  }

  isCollapsed(label: string): boolean {
    return this.collapsed.has(label);
  }

  toggleCollapsed(label: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.collapsed.has(label)) this.collapsed.delete(label);
    else this.collapsed.add(label);
  }

  collapseAll(): void {
    this.collapsed = new Set(this.rows.map(r => r.label));
  }

  expandAll(): void {
    this.collapsed.clear();
  }

  get allCollapsed(): boolean {
    return this.rows.length > 0 && this.collapsed.size === this.rows.length;
  }

  rangeStart!: Date;
  private totalDays = 1;
  private fullRangeStart!: Date;
  private fullRangeEnd!: Date;
  private rawGroups: Map<string, FracScheduleModel[]> = new Map();

  private readonly COLORS = {
    operating: '#04AC66',
    next7:     '#ff6600',
    next8_30:  '#ffae00',
    next31_60: '#8197f2',
    next61_90: '#6ec2f7',
    other:     '#9ca3af',
  };

  readonly legend = [
    { label: 'Operating',  color: this.COLORS.operating },
    { label: 'Next 7d',   color: this.COLORS.next7     },
    { label: '8–30d',     color: this.COLORS.next8_30  },
    { label: '31–60d',    color: this.COLORS.next31_60 },
    { label: '61–90d',    color: this.COLORS.next61_90 },
    { label: 'Other',     color: this.COLORS.other     },
  ];

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDetail();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) this.build();
  }

  setSortBy(sort: 'operator' | 'region'): void {
    if (this.sortBy === sort) return;
    this.sortBy = sort;
    this.collapsed.clear(); // labels change between operator/region grouping
    this.buildRawGroups();
    this.applyZoom();
  }

  setZoom(preset: ZoomPreset): void {
    if (this.zoomPreset === preset) return;
    this.zoomPreset = preset;
    this.applyZoom();
  }

  jumpToToday(): void {
    const el = this.calScrollRef?.nativeElement;
    if (!el) return;
    el.scrollTo({ left: Math.max(0, (this.todayPct / 100) * el.scrollWidth - el.clientWidth / 2), behavior: 'smooth' });
  }

  onBarClick(bar: TimelineBar, event: MouseEvent): void {
    if (this.selectedBar?.model.scheduleId === bar.model.scheduleId) {
      this.selectedBar = null;
      return;
    }
    this.selectedBar = bar;
    const wrap = (event.currentTarget as HTMLElement).closest('.cal-wrap') as HTMLElement;
    if (wrap) {
      const r = wrap.getBoundingClientRect();
      const x = Math.min(event.clientX - r.left + 12, r.width - 300);
      const rawY = event.clientY - r.top + 12;
      const y = rawY + 240 > r.height ? event.clientY - r.top - 252 : rawY;
      this.detailPanelStyle = { top: `${y}px`, left: `${x}px` };
    }
  }

  closeDetail(): void {
    this.selectedBar = null;
  }

  statusLabel(w: FracScheduleModel): string {
    if (w.operating)  return 'Operating';
    if (w.next7)      return 'Next 7d';
    if (w.next8_30)   return '8–30d';
    if (w.next31_60)  return '31–60d';
    if (w.next61_90)  return '61–90d';
    return 'Other';
  }

  fmtDate(d?: Date | string): string {
    if (!d) return '—';
    return new Date(d as any).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  getRowHeight(row: TimelineRow): number {
    if (this.isCollapsed(row.label)) return 28; // collapsed header height
    return row.rowCount * 26 + 10; // 26px per stacked bar + padding
  }

  trackByRow(_: number, row: TimelineRow): string {
    return row.label;
  }

  trackByBar(_: number, bar: TimelineBar): string {
    return bar.model.scheduleId;
  }

  // ── Build ────────────────────────────────────────────────────────────────

  private build(): void {
    if (!this.data?.length) { this.rows = []; this.monthLines = []; this.dateHeaders = []; return; }

    const today = this.startOfDay(new Date());

    const allDates: Date[] = [];
    for (const d of this.data) {
      if (d.fracStartDate) allDates.push(this.safeDate(d.fracStartDate));
      if (d.fracEndDate)   allDates.push(this.safeDate(d.fracEndDate));
    }
    allDates.push(today);

    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    this.fullRangeStart = new Date(minDate); this.fullRangeStart.setDate(this.fullRangeStart.getDate() - 4);
    this.fullRangeEnd   = new Date(maxDate); this.fullRangeEnd.setDate(this.fullRangeEnd.getDate() + 4);

    this.buildRawGroups();
    this.applyZoom();
  }

  private buildRawGroups(): void {
    this.rawGroups = new Map<string, FracScheduleModel[]>();
    for (const item of this.data) {
      const key = this.sortBy === 'operator'
        ? (item.companyName?.trim() || 'Unknown')
        : (item.region?.trim()      || 'No Region');
      if (!this.rawGroups.has(key)) this.rawGroups.set(key, []);
      this.rawGroups.get(key)!.push(item);
    }
  }

  private applyZoom(): void {
    if (!this.data?.length) return;

    const today = this.startOfDay(new Date());

    if (this.zoomPreset === 'all') {
      this.rangeStart = new Date(this.fullRangeStart);
      this.totalDays  = Math.max(1, this.daysBetween(this.rangeStart, this.fullRangeEnd));
      this.buildDateHeaders(this.fullRangeEnd);
      this.buildMonthLines(this.fullRangeEnd);
    } else {
      const days = this.zoomPreset === '30d' ? 30 : this.zoomPreset === '60d' ? 60 : 90;
      this.rangeStart = new Date(today); this.rangeStart.setDate(today.getDate() - Math.floor(days / 6));
      const rangeEnd  = new Date(this.rangeStart); rangeEnd.setDate(this.rangeStart.getDate() + days);
      this.totalDays  = days;
      this.buildDateHeaders(rangeEnd);
      this.buildMonthLines(rangeEnd);
    }

    this.todayPct = this.toPct(today);
    this.buildProjectedRows();
  }

  private buildDateHeaders(rangeEnd: Date): void {
    const step = this.totalDays > 180 ? 14 : this.totalDays > 90 ? 7 : this.totalDays > 30 ? 3 : 1;
    this.dateHeaders = [];
    const cur = new Date(this.rangeStart);
    while (cur <= rangeEnd) {
      this.dateHeaders.push({
        label:   cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        leftPct: this.toPct(cur),
      });
      cur.setDate(cur.getDate() + step);
    }
  }

  private buildMonthLines(rangeEnd: Date): void {
    this.monthLines = [];
    const cur = new Date(this.rangeStart.getFullYear(), this.rangeStart.getMonth() + 1, 1);
    while (cur <= rangeEnd) {
      this.monthLines.push({
        leftPct: this.toPct(cur),
        label: cur.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      });
      cur.setMonth(cur.getMonth() + 1);
    }
  }

  private buildProjectedRows(): void {
    this.rows = Array.from(this.rawGroups.keys()).sort().map(label => {
      const allWells = this.rawGroups.get(label)!.filter(w => w.fracStartDate);
      // Filter to wells visible in the current window
      const wells = allWells.filter(w => {
        const start = this.safeDate(w.fracStartDate);
        const end = w.fracEndDate
          ? this.safeDate(w.fracEndDate)
          : new Date(start.getTime() + 864e5);
        return this.toPct(end) >= 0 && this.toPct(start) <= 100;
      });
      const { bars, rowCount } = this.stackBars(wells);
      return { label, wells: bars, rowCount };
    });
  }

  private stackBars(wells: FracScheduleModel[]): { bars: TimelineBar[]; rowCount: number } {
    const sorted = [...wells].sort((a, b) =>
      this.safeDate(a.fracStartDate).getTime() - this.safeDate(b.fracStartDate).getTime()
    );

    const bars: TimelineBar[] = [];
    const rowEnds: number[] = [];

    for (const well of sorted) {
      const start = this.safeDate(well.fracStartDate);
      const end   = well.fracEndDate
        ? this.safeDate(well.fracEndDate)
        : new Date(start.getTime() + 864e5);

      const leftPct  = Math.max(0, this.toPct(start));
      const rightPct = Math.min(100, this.toPct(end) + (1 / this.totalDays * 100));
      const widthPct = Math.max(0.3, rightPct - leftPct);

      let stackRow = 0;
      while (rowEnds[stackRow] !== undefined && rowEnds[stackRow] > leftPct - 0.5) {
        stackRow++;
      }
      rowEnds[stackRow] = leftPct + widthPct;

      bars.push({ model: well, leftPct, widthPct, color: this.colorFor(well), stackRow });
    }

    return { bars, rowCount: Math.max(1, rowEnds.length) };
  }

  private colorFor(w: FracScheduleModel): string {
    if (w.operating) return this.COLORS.operating;
    if (w.next7)     return this.COLORS.next7;
    if (w.next8_30)  return this.COLORS.next8_30;
    if (w.next31_60) return this.COLORS.next31_60;
    if (w.next61_90) return this.COLORS.next61_90;
    return this.COLORS.other;
  }

  private toPct(date: Date): number {
    return (this.daysBetween(this.rangeStart, date) / this.totalDays) * 100;
  }

  private daysBetween(a: Date, b: Date): number {
    return (b.getTime() - a.getTime()) / 864e5;
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Parse a Date, ISO string, or date-only string safely into a local-midnight Date. */
  private safeDate(d: Date | string | undefined | null): Date {
    if (!d) return new Date(NaN);
    if (d instanceof Date) return this.startOfDay(d);
    const s = String(d);
    // Date-only "YYYY-MM-DD" → force local-time parse (avoids UTC shift)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return this.startOfDay(new Date(s + 'T00:00:00'));
    }
    // ISO or other format → parse normally, then normalize to midnight
    return this.startOfDay(new Date(s));
  }
}
